import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import SparkelIcon from '../../common/icon/SparkelIcon';
import {
  AllocateProjectCreditsDocument,
  DeallocateProjectCreditsDocument,
  GetProjectCreditsDocument,
  GetTenantCreditsDocument,
} from 'src/gql/graphql';

type CreditsAllocationTabProps = {
  projectId: string;
  projectName: string;
};

// eslint-disable-next-line complexity
export default function CreditsAllocationTab({
  projectId,
  projectName,
}: CreditsAllocationTabProps) {
  const { t } = useTranslation('company');
  const toast = useToast();
  const [allocateAmount, setAllocateAmount] = useState(10);
  const [deallocateAmount, setDeallocateAmount] = useState(10);

  const [{ data: tenantData, fetching: tenantFetching }] = useQuery({
    query: GetTenantCreditsDocument,
  });

  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: GetProjectCreditsDocument,
    variables: { projectId },
  });

  const [{ fetching: allocating }, allocateCredits] = useMutation(
    AllocateProjectCreditsDocument
  );
  const [{ fetching: deallocating }, deallocateCredits] = useMutation(
    DeallocateProjectCreditsDocument
  );

  const tenantCredits = tenantData?.myTenantCredits;
  const projectCredits = projectData?.myProjectCredits;
  const hasAllocation = !!projectCredits;

  const handleAllocate = async () => {
    if (allocateAmount <= 0) return;

    try {
      const { error } = await allocateCredits({
        input: { projectId, amount: allocateAmount },
      });
      if (error) throw error;

      toast({
        title: t('project-settings-modal.credits.success.allocated', {
          amount: allocateAmount,
        }),
        status: 'success',
        duration: 3000,
      });
      setAllocateAmount(10);
    } catch (error) {
      toast({
        title: t('project-settings-modal.credits.error.allocate-failed'),
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeallocate = async () => {
    if (deallocateAmount <= 0) return;

    try {
      const { error } = await deallocateCredits({
        input: { projectId, amount: deallocateAmount },
      });
      if (error) throw error;

      toast({
        title: t('project-settings-modal.credits.success.deallocated', {
          amount: deallocateAmount,
        }),
        status: 'success',
        duration: 3000,
      });
      setDeallocateAmount(10);
    } catch (error) {
      toast({
        title: t('project-settings-modal.credits.error.deallocate-failed'),
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (tenantFetching || projectFetching) {
    return (
      <Flex justifyContent="center" padding={8}>
        <Spinner />
      </Flex>
    );
  }

  if (!tenantCredits) {
    return (
      <Alert status="warning">
        <AlertIcon />
        {t('project-settings-modal.credits.no-credits-system')}
      </Alert>
    );
  }

  if (tenantCredits.unlimited) {
    return (
      <Alert status="info">
        <AlertIcon />
        {t('project-settings-modal.credits.unlimited-info')}
      </Alert>
    );
  }

  const usedPercentage = projectCredits
    ? Math.round(
        (projectCredits.usedCredits / projectCredits.allocatedCredits) * 100
      )
    : 0;

  const maxAllocate = tenantCredits.availableCredits;
  const maxDeallocate = projectCredits
    ? projectCredits.allocatedCredits - projectCredits.usedCredits
    : 0;

  return (
    <VStack spacing={6} alignItems="stretch">
      {/* Current Status */}
      <Box
        borderWidth={1}
        borderRadius="md"
        padding={4}
        backgroundColor="gray.50"
        _dark={{ backgroundColor: 'gray.700' }}
      >
        <Text fontWeight="medium" marginBottom={3}>
          {t('project-settings-modal.credits.current-status')}
        </Text>

        {hasAllocation ? (
          <HStack spacing={8}>
            <Stat>
              <StatLabel>
                {t('project-settings-modal.credits.allocated')}
              </StatLabel>
              <StatNumber>{projectCredits.allocatedCredits}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t('project-settings-modal.credits.used')}</StatLabel>
              <StatNumber color={usedPercentage >= 90 ? 'red.500' : undefined}>
                {projectCredits.usedCredits}
              </StatNumber>
              <StatHelpText>
                <Progress
                  value={usedPercentage}
                  colorScheme={
                    usedPercentage >= 90
                      ? 'red'
                      : usedPercentage >= 70
                      ? 'orange'
                      : 'green'
                  }
                  size="sm"
                  borderRadius="full"
                  marginTop={1}
                />
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>
                {t('project-settings-modal.credits.available')}
              </StatLabel>
              <StatNumber color="green.500">
                {projectCredits.availableCredits}
              </StatNumber>
            </Stat>
          </HStack>
        ) : (
          <Text color="gray.500">
            {t('project-settings-modal.credits.no-allocation')}
          </Text>
        )}
      </Box>

      {/* Company Pool Info */}
      <Box>
        <Text fontSize="sm" color="gray.600" marginBottom={2}>
          {t('project-settings-modal.credits.company-pool')}
        </Text>
        <HStack>
          <Badge colorScheme="blue" fontSize="sm" padding={2}>
            {tenantCredits.availableCredits}{' '}
            {t('project-settings-modal.credits.available-in-pool')}
          </Badge>
          <Text fontSize="sm" color="gray.500">
            {t('project-settings-modal.credits.of-total', {
              total: tenantCredits.totalCredits,
            })}
          </Text>
        </HStack>
      </Box>

      {/* Allocate Credits */}
      <Box>
        <FormControl>
          <FormLabel>
            {t('project-settings-modal.credits.allocate-credits')}
          </FormLabel>
          <HStack>
            <NumberInput
              value={allocateAmount}
              onChange={(_, value) => setAllocateAmount(value || 0)}
              min={1}
              max={maxAllocate}
              flex={1}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Button
              leftIcon={<SparkelIcon icon={faPlus} />}
              colorScheme="blue"
              onClick={handleAllocate}
              isLoading={allocating}
              isDisabled={allocateAmount <= 0 || allocateAmount > maxAllocate}
            >
              {t('project-settings-modal.credits.allocate')}
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.500" marginTop={1}>
            {t('project-settings-modal.credits.max-available', {
              count: maxAllocate,
            })}
          </Text>
        </FormControl>
      </Box>

      {/* Deallocate Credits */}
      {hasAllocation && maxDeallocate > 0 && (
        <Box>
          <FormControl>
            <FormLabel>
              {t('project-settings-modal.credits.deallocate-credits')}
            </FormLabel>
            <HStack>
              <NumberInput
                value={deallocateAmount}
                onChange={(_, value) => setDeallocateAmount(value || 0)}
                min={1}
                max={maxDeallocate}
                flex={1}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Button
                leftIcon={<SparkelIcon icon={faMinus} />}
                colorScheme="orange"
                variant="outline"
                onClick={handleDeallocate}
                isLoading={deallocating}
                isDisabled={
                  deallocateAmount <= 0 || deallocateAmount > maxDeallocate
                }
              >
                {t('project-settings-modal.credits.deallocate')}
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" marginTop={1}>
              {t('project-settings-modal.credits.max-deallocate', {
                count: maxDeallocate,
              })}
            </Text>
          </FormControl>
        </Box>
      )}

      {/* Help text */}
      <Text fontSize="sm" color="gray.500">
        {t('project-settings-modal.credits.help-text')}
      </Text>
    </VStack>
  );
}
